import {
  OuterPopup,
  InnerPopup,
  Indent,
  CloseButton,
  InfoRow,
  Label,
  Value,
  Dots,
  ToggleContainer,
  ToggleInput,
  ToggleSlider,
} from "./MyAccount.styled";
import {
  useAccountContext,
  cognitoUserAttributes,
  useUserDataContext,
} from "../../../contexts/index";
import { useEffect, useState } from "react";
import { createApiClient, formatDateToMMDDYYYY } from "../../../utils/index";
import { ImpersonationPanel } from "./ImpersonationPanel";
import { LoadingPopup, UpdateAdminPopup } from "../../modals";

type InfoLineProps = {
  label: React.ReactNode;
  value: string | JSX.Element;
};

interface ClientOptions {
  value: string;
  label: string;
}

interface AccountResponseModel {
  Clients: SubscriptionModel[];
  Subscription: SubscriptionModel;
  TotalClients: number;
}

interface SubscriptionModel {
  AccountType: string;
  CancelAtPeriodEnd: boolean;
  CreatedOn: string;
  Email: string;
  LastLogin: string;
  LastUpdate: string;
  SubscriptionEndDate: string;
  SubscriptionStatus: string;
  UserId: string;
  AdminAuthStatus: boolean;
  ClientAdmin: string;
}

const InfoLine: React.FC<InfoLineProps> = ({ label, value }) => (
  <InfoRow>
    <Label>{label}</Label>
    <Dots />
    <Value>{value}</Value>
  </InfoRow>
);

export function MyAccount() {
  const {
    setMyAccountOpen,
    userCognitoAttrubutes,
    loggedInUserCognitoIdToken,
    getRefreshedToken,
  } = useAccountContext();
  const { itemMap, searchMap } = useUserDataContext();
  const [subscriberData, setSubscriberData] = useState<
    SubscriptionModel | undefined
  >();
  const [clients, setClients] = useState<SubscriptionModel[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOptions[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>(
    (userCognitoAttrubutes as cognitoUserAttributes).email ?? ""
  );
  const [totalAssociatedAccounts, setTotalAssociatedAccounts] = useState(0);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isUpdateAdminModalOpen, setIsUpdateAdminModalOpen] = useState(false);

  useEffect(() => {
    if (!loggedInUserCognitoIdToken) {
      setIsLoadingSubscription(false);
      return;
    }

    const fetchData = async () => {
      setIsLoadingSubscription(true);
      try {
        const token = await getRefreshedToken();
        const api = createApiClient(token);
        const response = await api.get<AccountResponseModel>(
          process.env.REACT_APP_AWS_SUBSCRIPTION_API_URL!
        );
        console.log(response);
        setSubscriberData(response.data.Subscription);
        setClients(response.data.Clients);
        setTotalAssociatedAccounts(response.data.TotalClients);
      } catch (err) {
        console.error("Axios Error:", err);
        // On error, we still show the modal with whatever data is available
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    fetchData();
  }, []);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClientId(e.target.value);
    console.log("Selected client ID:", e.target.value);
  };

  const updateAdminAccess = () => {
    const fetchData = async () => {
      try {
        const token = await getRefreshedToken();
        const api = createApiClient(token);
        const response = await api.put<AccountResponseModel>(
          `${process.env.REACT_APP_AWS_SUBSCRIPTION_API_URL}/toggleadminaccess`
        );
        console.log(response);
        setSubscriberData(response.data.Subscription)
      } catch (err) {
        console.error("Axios Error:", err);
      }
    };
    fetchData();
  }

  // Show loading popup while fetching subscription data
  if (isLoadingSubscription) {
    return <LoadingPopup message="Loading account details..." />;
  }

  return (
    <OuterPopup>
      <InnerPopup data-scroll-lock-scrollable="true">
        <h3>My Account: </h3>
        <InfoLine
          label="Email Address:"
          value={(userCognitoAttrubutes as cognitoUserAttributes).email ?? ""}
        />
        <InfoLine
          label="Item Count:"
          value={Object.keys(itemMap).length.toString()}
        />
        <InfoLine
          label="Items In Use:"
          value={Object.values(itemMap).filter((item) => item.InUse).length.toString()}
        />
        <InfoLine
          label="Search Term Count:"
          value={Object.keys(searchMap).length.toString()}
        />
        <InfoLine
          label="Account Status:"
          value={subscriberData?.SubscriptionStatus?.toUpperCase() ?? ""}
        />
        <InfoLine
          label="Account Type:"
          value={subscriberData?.AccountType ?? ""}
        />
        <div>
          <strong>Subscription Status:</strong>
          <Indent>
            <InfoLine
              label="Next Payment Date:"
              value={
                subscriberData?.SubscriptionEndDate
                  ? formatDateToMMDDYYYY(subscriberData.SubscriptionEndDate)
                  : ""
              }
            />
          </Indent>
          <Indent>
            <InfoLine
              label="Account Creation Date:"
              value={
                subscriberData?.CreatedOn
                  ? formatDateToMMDDYYYY(subscriberData.CreatedOn)
                  : ""
              }
            />
          </Indent>
        </div>
        {subscriberData?.AccountType?.toLocaleLowerCase() != "admin" && (<div>
          <strong>Administrator Status:</strong>
          <Indent>
            <InfoLine
              label="Current Admin:"
              value={subscriberData?.ClientAdmin ?? ""}
            />
          </Indent>
          <Indent>
            <InfoLine
              label="Allow Admin Access:"
              value={
                <ToggleContainer>
                  <ToggleInput
                    type="checkbox"
                    checked={subscriberData?.AdminAuthStatus ?? false}
                    onChange={updateAdminAccess}
                  />
                  <ToggleSlider />
                </ToggleContainer>
              }
            />
          </Indent>
          {subscriberData?.ClientAdmin && (
            <Indent style={{ display: "flex", justifyContent: "flex-end" }}>
              <CloseButton
                onClick={() => setIsUpdateAdminModalOpen(true)}
                style={{ marginTop: "10px" }}
              >
                Update Administrator
              </CloseButton>
            </Indent>
          )}
          {!subscriberData?.ClientAdmin && (
            <Indent style={{ display: "flex", justifyContent: "flex-end" }}>
              <CloseButton
                onClick={() => setIsUpdateAdminModalOpen(true)}
                style={{ marginTop: "10px" }}
              >
                Add Administrator
              </CloseButton>
            </Indent>
          )}
        </div>)}
        {totalAssociatedAccounts > 0 && (
          <InfoLine
            label={<strong>Total Associated Accounts:</strong>}
            value={totalAssociatedAccounts.toString()}
          />
        )}
        {clients.length > 0 && <ImpersonationPanel clients={clients} />}
        <CloseButton onClick={() => setMyAccountOpen(false)}>Close</CloseButton>
      </InnerPopup>
      {isUpdateAdminModalOpen && (
        <UpdateAdminPopup
          currentAdmin={subscriberData?.ClientAdmin ?? ""}
          onClose={() => setIsUpdateAdminModalOpen(false)}
        />
      )}
    </OuterPopup>
  );
}
